import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  IconButton,
  Paper,
  Chip,
} from '@mui/material';
import {
  Send as SendIcon,
  ChatBubbleOutline,
  Phone as PhoneIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { chatService, Message as ChatServiceMessage, Thread } from '../services/chatService';
import { Community } from '../services/communityService';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { useCommunity } from '../contexts/CommunityContext';
import { useSidebar } from '../contexts/SidebarContext';
import MediaUploadButton from '../components/communication/MediaUploadButton';
import MediaMessageRenderer from '../components/communication/MediaMessageRenderer';
import MediaViewer from '../components/communication/MediaViewer';
import { MessagingThreadList } from '../components/communication/MessagingThreadList';
import { MentionRenderer } from '../utils/mentionRenderer';
import { AppVoiceCallPanel } from '../components/communication/AppVoiceCallPanel';
import { ChatPaymentBubble } from '../components/chat/ChatPaymentBubble';
import { CWY_COLORS } from '../theme/colors';
import {
  appTwilioVoiceService,
  type AppVoiceState,
} from '../services/appTwilioVoiceService';
import { parseChatTopicFromSearchParams } from '../config/serviceTopicRoutes';
import { CommunityInviteJoinModal } from '../components/community/CommunityInviteJoinModal';
import { fetchInvitePreview, type InvitePreview } from '../services/communityService';
import { openChatPaymentCheckout } from '../services/chatPaymentService';
import { resolveChatPaymentDisplayStatus } from '../utils/chatPaymentDisplay';
import { consumePaymentResultSeen } from '../utils/paymentResultSeen';
import { findExistingVehicleThread, vehicleThreadTags } from '../utils/chatTopicThread';

const SUPPORT_DISPLAY_NAME = 'CWY Support';

interface ChatSectionMessage {
  id: string;
  content: string;
  timestamp: string;
  from: string;
  type: 'incoming' | 'outgoing';
  status?: 'sending' | 'sent' | 'failed';
  error?: string;
  media_url?: string;
  message_type?: 'text' | 'image' | 'document' | 'audio' | 'video' | 'sticker' | 'call' | 'payment' | string;
  filename?: string;
  mime_type?: string;
  file_size?: number;
  metadata?: Record<string, any>;
}

function getDateLabel(date: Date) {
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  ) {
    return 'Today';
  } else if (
    date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString();
  }
}

const getMessageDate = (msg: ChatSectionMessage) => {
  if (/^\d+$/.test(msg.timestamp)) {
    return new Date(Number(msg.timestamp) * 1000);
  }
  return new Date(msg.timestamp);
};

function formatCallDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}


export const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { client } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    threads,
    selectedThread,
    setSelectedThread,
    loadingThreads,
    refreshThreads,
    markThreadRead,
    exitNewChatMode,
    isNewChatMode,
    pendingTopic,
    clearPendingTopic,
    startTopicChat,
  } = useChat();
  const {
    communities,
    loadingCommunities,
    unreadMap,
    selectCommunity,
    clearCommunitySelection,
  } = useCommunity();
  const { isMobile } = useSidebar();
  const [messages, setMessages] = useState<ChatServiceMessage[]>([]);
  const [pendingMessages, setPendingMessages] = useState<Map<string, ChatSectionMessage>>(new Map());
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const markReadTimerRef = useRef<number | undefined>(undefined);
  
  // Media viewer state
  const [viewerOpen, setViewerOpen] = useState(false);
  const [voicePanelOpen, setVoicePanelOpen] = useState(false);
  const [voiceState, setVoiceState] = useState<AppVoiceState>(() => appTwilioVoiceService.getState());
  const [isVoiceMuted, setIsVoiceMuted] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    type: string;
    filename?: string;
    mimeType?: string;
  } | null>(null);
  const [inviteModalToken, setInviteModalToken] = useState<string | null>(null);
  const [inviteModalPreview, setInviteModalPreview] = useState<InvitePreview | null>(null);
  const [inviteModalLoading, setInviteModalLoading] = useState(false);
  const [payingTransactionId, setPayingTransactionId] = useState<string | null>(null);
  const payingTransactionIdRef = useRef<string | null>(null);
  const [celebrateTransactionId, setCelebrateTransactionId] = useState<string | null>(null);

  useEffect(() => {
    const openInviteToken = (location.state as { openInviteToken?: string } | null)?.openInviteToken;
    if (!openInviteToken) return;

    setInviteModalToken(openInviteToken);
    setInviteModalLoading(true);
    setInviteModalPreview(null);
    navigate('/chat', { replace: true, state: {} });

    void fetchInvitePreview(openInviteToken)
      .then((preview) => setInviteModalPreview(preview))
      .catch(() => setInviteModalPreview(null))
      .finally(() => setInviteModalLoading(false));
  }, [location.state, navigate]);

  useEffect(() => {
    const topicFromUrl = parseChatTopicFromSearchParams(searchParams);
    if (topicFromUrl) {
      startTopicChat(topicFromUrl);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, startTopicChat]);

  useEffect(() => {
    if (!pendingTopic?.vehicleStock || selectedThread || loadingThreads) return;
    if (pendingTopic.forceNewThread === true) return;
    const existing = findExistingVehicleThread(threads, pendingTopic.topicKey, pendingTopic.vehicleStock, pendingTopic.vehicleId);
    if (!existing) return;
    clearPendingTopic();
    exitNewChatMode();
    setSelectedThread(existing);
  }, [
    pendingTopic,
    selectedThread,
    loadingThreads,
    threads,
    clearPendingTopic,
    exitNewChatMode,
    setSelectedThread,
  ]);

  useEffect(() => {
    if (!pendingTopic?.draftMessage || selectedThread || !isNewChatMode) return;
    setNewMessage(pendingTopic.draftMessage);
  }, [pendingTopic?.draftMessage, pendingTopic?.vehicleStock, selectedThread, isNewChatMode]);

  useEffect(() => {
    return appTwilioVoiceService.subscribe((s) => {
      setVoiceState(s);
      setIsVoiceMuted(appTwilioVoiceService.isMuted());
    });
  }, []);

  const prevVoiceStatusRef = useRef<AppVoiceState['status']>(appTwilioVoiceService.getState().status);
  const selectedThreadRef = useRef(selectedThread);
  const clientRef = useRef(client);
  selectedThreadRef.current = selectedThread;
  clientRef.current = client;

  /** Voice lifecycle: close empty panel on idle; log `call` message after a real session ends. */
  useEffect(() => {
    const prev = prevVoiceStatusRef.current;
    const status = voiceState.status;
    prevVoiceStatusRef.current = status;

    if (voicePanelOpen && status === 'idle' && prev !== 'idle') {
      setVoicePanelOpen(false);
    }

    const terminal =
      (status === 'idle' && prev !== 'idle') || (status === 'error' && prev !== 'error');

    if (!terminal) return;

    const summary = appTwilioVoiceService.consumeLastSupportCallSession();
    if (!summary) return;

    const thread = selectedThreadRef.current;
    const cl = clientRef.current;
    if (!thread || !cl) return;

    void (async () => {
      try {
        const sent = await chatService.sendCallLogMessage(
          thread.thread_id,
          cl._id,
          cl.client_id,
          { durationSec: summary.durationSec, outcome: summary.outcome }
        );
        if (sent?.thread_id === thread.thread_id) {
          setMessages((prevMsgs) => {
            const id = sent._id;
            if (id && prevMsgs.some((m) => m._id === id)) return prevMsgs;
            return [...prevMsgs, sent];
          });
        }
        refreshThreads();
      } catch (err) {
        console.warn('[ChatPage] Failed to log support call message', err);
      }
    })();
  }, [voiceState.status, voicePanelOpen, refreshThreads]);

  // Deep-link: /chat?thread=<thread_id> (e.g. after chat payment result)
  useEffect(() => {
    const threadFromUrl = searchParams.get('thread');
    if (!threadFromUrl || loadingThreads || threads.length === 0) return;
    const match = threads.find((t) => t.thread_id === threadFromUrl);
    if (!match) return;
    if (selectedThread?.thread_id === match.thread_id) return;
    setSelectedThread(match);
    exitNewChatMode();
    clearCommunitySelection();
  }, [
    searchParams,
    threads,
    loadingThreads,
    selectedThread?.thread_id,
    setSelectedThread,
    exitNewChatMode,
    clearCommunitySelection,
  ]);

  // Auto-select first thread on desktop if none selected (but not in new chat mode)
  useEffect(() => {
    if (searchParams.get('thread')) return;
    if (!isMobile && !selectedThread && threads.length > 0 && !isNewChatMode) {
      setSelectedThread(threads[0]);
    }
  }, [threads, selectedThread, setSelectedThread, isNewChatMode, isMobile, searchParams]);

  const scheduleMarkThreadRead = useCallback(
    (threadId: string, unread?: number) => {
      if ((unread ?? 0) <= 0) return;
      if (markReadTimerRef.current) {
        window.clearTimeout(markReadTimerRef.current);
      }
      markReadTimerRef.current = window.setTimeout(() => {
        void markThreadRead(threadId);
      }, 500);
    },
    [markThreadRead]
  );

  useEffect(() => {
    return () => {
      if (markReadTimerRef.current) {
        window.clearTimeout(markReadTimerRef.current);
      }
    };
  }, []);

  const loadMessages = async (threadId: string, unreadCount?: number) => {
    try {
      setLoadingMessages(true);
      const messagesData = await chatService.getMessages(threadId);
      setMessages(messagesData);
      setPendingMessages(new Map());
      scheduleMarkThreadRead(threadId, unreadCount);
    } catch (err: any) {
      setError(err.message || 'Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Load messages when thread is selected
  useEffect(() => {
    if (selectedThread) {
      void loadMessages(selectedThread.thread_id, selectedThread.unread_count);
    } else {
      setMessages([]);
    }
  }, [selectedThread]);

  // Socket listener for new messages
  useEffect(() => {
    if (!selectedThread) return;

    const unsubscribe = chatService.onNewMessage((rawMessage) => {
      // Normalize: socket payload may send 'id' instead of '_id'
      const message: ChatServiceMessage = {
        ...rawMessage,
        _id: rawMessage._id || (rawMessage as any).id || (rawMessage as any).message_id,
      };
      if (message.thread_id === selectedThread.thread_id) {
        setMessages((prev) => {
          const msgId = message._id;
          if (msgId && prev.some(m => m._id === msgId)) return prev;
          return [...prev, message];
        });
        if (message.direction === 'outbound') {
          scheduleMarkThreadRead(selectedThread.thread_id, selectedThread.unread_count ?? 1);
        }
        setPendingMessages((prev) => {
          const newMap = new Map(prev);
          newMap.delete(message._id);
          newMap.forEach((pendingMsg, key) => {
            if (pendingMsg.content === message.content && 
                pendingMsg.from === message.from &&
                Math.abs(new Date(pendingMsg.timestamp).getTime() - new Date(message.timestamp).getTime()) < 5000) {
              newMap.delete(key);
            }
          });
          return newMap;
        });
      }
    });

    return unsubscribe;
  }, [selectedThread, scheduleMarkThreadRead]);

  // Socket listener for payment status (and other message) updates
  useEffect(() => {
    if (!selectedThread) return;

    const unsubscribe = chatService.onMessageUpdated((rawMessage) => {
      const messageId =
        rawMessage._id || (rawMessage as any).id || (rawMessage as any).message_id;
      const threadId = rawMessage.thread_id || selectedThread.thread_id;
      if (!messageId || threadId !== selectedThread.thread_id) return;

      const nextStatus = rawMessage.metadata?.payment_status as string | undefined;
      const txnId = rawMessage.metadata?.transaction_id as string | undefined;
      if (
        txnId &&
        (nextStatus === 'confirmed' || nextStatus === 'verified') &&
        !consumePaymentResultSeen(txnId)
      ) {
        setCelebrateTransactionId(txnId);
      }

      setMessages((prev) =>
        prev.map((m) => {
          const ids = [
            m._id,
            (m as any).id,
            (m as any).message_id,
          ]
            .filter(Boolean)
            .map((v) => String(v));
          if (!ids.includes(String(messageId))) return m;
          return {
            ...m,
            ...rawMessage,
            _id: m._id || messageId,
            message_id: (m as any).message_id || (rawMessage as any).message_id || messageId,
            content: rawMessage.content ?? m.content,
            message_type: rawMessage.message_type ?? m.message_type,
            metadata: rawMessage.metadata ?? m.metadata,
          };
        })
      );
    });

    return unsubscribe;
  }, [selectedThread]);

  const markPaymentCompleted = useCallback((transactionId: string, status?: string) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.metadata?.transaction_id !== transactionId) return m;
        return {
          ...m,
          metadata: {
            ...(m.metadata || {}),
            payment_status: status === 'verified' ? 'verified' : 'confirmed',
          },
        };
      })
    );
    setCelebrateTransactionId(
      consumePaymentResultSeen(transactionId) ? null : transactionId
    );
  }, []);

  const handlePayPaymentMessage = useCallback(async (transactionId: string) => {
    if (!transactionId || payingTransactionIdRef.current) {
      return;
    }
    payingTransactionIdRef.current = transactionId;
    setPayingTransactionId(transactionId);
    setError('');

    try {
      const result = await openChatPaymentCheckout(transactionId);
      if (result.already_paid) {
        markPaymentCompleted(transactionId, result.status);
        return;
      }
      if (!result.checkout_url) {
        throw new Error(result.error || 'Checkout link is not available');
      }
      if (result.expires_at) {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.metadata?.transaction_id !== transactionId) return m;
            return {
              ...m,
              metadata: {
                ...(m.metadata || {}),
                expires_at: result.expires_at,
              },
            };
          })
        );
      }
      // Same-tab hosted Checkout → Stripe → /payment/result → back to chat
      window.location.assign(result.checkout_url);
    } catch (err: unknown) {
      const apiMsg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      const message = apiMsg || (err instanceof Error ? err.message : 'Failed to open payment');
      setError(message);
      if (/expired/i.test(message)) {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.metadata?.transaction_id !== transactionId) return m;
            return {
              ...m,
              metadata: {
                ...(m.metadata || {}),
                payment_status: 'expired',
              },
            };
          })
        );
      }
    } finally {
      payingTransactionIdRef.current = null;
      setPayingTransactionId(null);
    }
  }, [markPaymentCompleted]);

  // Transform messages for display
  const chatSectionMessages = useMemo(() => {
    const transformed: ChatSectionMessage[] = [];
    
    pendingMessages.forEach((msg) => {
      transformed.push(msg);
    });

    messages.forEach((msg) => {
      const msgId = msg._id || (msg as any).id || (msg as any).message_id;
      if (pendingMessages.has(msgId)) return;
      const mediaUrl = msg.media?.blob_url || msg.media?.file_url;
      transformed.push({
        id: msgId,
        content: msg.content,
        timestamp: msg.timestamp,
        from: msg.from || client?._id || '',
        type: msg.direction === 'inbound' ? 'outgoing' : 'incoming',
        status: msg.status === 'sent' ? 'sent' : 'sent',
        media_url: mediaUrl,
        message_type: msg.message_type,
        filename: msg.media?.filename,
        mime_type: msg.media?.mime_type,
        file_size: msg.media?.size,
        metadata: msg.metadata,
      });
    });

    return transformed;
  }, [messages, pendingMessages, client?._id]);

  // Sort and group messages
  const messagesWithSeparators = useMemo(() => {
    const sorted = [...chatSectionMessages].sort((a, b) => {
      const aTime = getMessageDate(a).getTime();
      const bTime = getMessageDate(b).getTime();
      return aTime - bTime;
    });

    const result: Array<{ type: 'separator'; label: string } | { type: 'message'; message: ChatSectionMessage }> = [];
    let lastDateLabel = '';
    
    sorted.forEach((msg) => {
      const dateObj = getMessageDate(msg);
      const isValidDate = !isNaN(dateObj.getTime());
      if (!isValidDate) return;
      const dateLabel = getDateLabel(dateObj);
      if (dateLabel !== lastDateLabel) {
        result.push({ type: 'separator', label: dateLabel });
        lastDateLabel = dateLabel;
      }
      result.push({ type: 'message', message: msg });
    });
    
    return result;
  }, [chatSectionMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messagesWithSeparators.length]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread || !client || sending) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    
    const tempId = `temp-${Date.now()}`;
    const optimisticMessage: ChatSectionMessage = {
      id: tempId,
      content: messageContent,
      timestamp: new Date().toISOString(),
      from: client._id,
      type: 'outgoing',
      status: 'sending',
    };

    setPendingMessages((prev) => {
      const newMap = new Map(prev);
      newMap.set(tempId, optimisticMessage);
      return newMap;
    });

    try {
      setSending(true);
      const sentMessage = await chatService.sendMessage(
        selectedThread.thread_id,
        messageContent,
        client._id
      );
      
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        newMap.set(sentMessage._id, {
          ...optimisticMessage,
          id: sentMessage._id,
          status: 'sent',
        });
        return newMap;
      });

      refreshThreads();
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        const failedMsg = newMap.get(tempId);
        if (failedMsg) {
          newMap.set(tempId, {
            ...failedMsg,
            status: 'failed',
            error: err.message || 'Failed to send',
          });
        }
        return newMap;
      });
    } finally {
      setSending(false);
    }
  };

  const handleMediaSend = async (file: File, mediaType: string) => {
    if (!selectedThread || !client) return;

    const tempId = `temp-media-${Date.now()}`;
    const localPreviewUrl = URL.createObjectURL(file);
    
    const optimisticMessage: ChatSectionMessage = {
      id: tempId,
      content: file.name,
      timestamp: new Date().toISOString(),
      from: client._id,
      type: 'outgoing',
      status: 'sending',
      media_url: localPreviewUrl,
      message_type: mediaType as any,
      filename: file.name,
      mime_type: file.type,
      file_size: file.size,
    };

    setPendingMessages((prev) => {
      const newMap = new Map(prev);
      newMap.set(tempId, optimisticMessage);
      return newMap;
    });

    try {
      setSending(true);
      const sentMessage = await chatService.sendMediaMessage(
        selectedThread.thread_id,
        file,
        mediaType,
        client._id,
        client.client_id
      );
      
      URL.revokeObjectURL(localPreviewUrl);
      const realMediaUrl = sentMessage.media?.blob_url || sentMessage.media?.file_url;
      
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        newMap.delete(tempId);
        newMap.set(sentMessage._id, {
          ...optimisticMessage,
          id: sentMessage._id,
          content: sentMessage.content || file.name,
          status: 'sent',
          media_url: realMediaUrl || localPreviewUrl,
          filename: sentMessage.media?.filename || file.name,
          mime_type: sentMessage.media?.mime_type || file.type,
          file_size: sentMessage.media?.size || file.size,
        });
        return newMap;
      });

      refreshThreads();
    } catch (err: any) {
      URL.revokeObjectURL(localPreviewUrl);
      setError(err.message || 'Failed to send media');
      setPendingMessages((prev) => {
        const newMap = new Map(prev);
        const failedMsg = newMap.get(tempId);
        if (failedMsg) {
          newMap.set(tempId, { ...failedMsg, status: 'failed', error: err.message || 'Failed to send' });
        }
        return newMap;
      });
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = async () => {
    if (!client || !newMessage.trim()) return;

    setSending(true);
    setError('');

    try {
      if (pendingTopic) {
        const thread = await chatService.createTopicThread({
          category: pendingTopic.topicKey,
          title: pendingTopic.title,
          clientId: client.client_id,
          forceNew: pendingTopic.forceNewThread === true,
          tags: pendingTopic.vehicleStock ? vehicleThreadTags(pendingTopic.vehicleStock, pendingTopic.vehicleId) : undefined,
        });
        clearPendingTopic();
        await chatService.sendMessage(thread.thread_id, newMessage.trim(), client._id);
        setNewMessage('');
        setSelectedThread(thread);
        await refreshThreads();
      } else {
        await chatService.startNewChat(client._id, client.client_id, newMessage.trim());
        setNewMessage('');
        const newThreads = await chatService.getThreads();
        if (newThreads.length > 0) {
          setSelectedThread(newThreads[0]);
        }
        await refreshThreads();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to start chat');
    } finally {
      setSending(false);
    }
  };

  const handleCallSupport = async () => {
    if (!client || !selectedThread) return;

    if (voiceState.status !== 'idle' && voiceState.status !== 'error') {
      setVoicePanelOpen(true);
      return;
    }

    setVoicePanelOpen(true);
    setError('');
    try {
      await appTwilioVoiceService.startSupportCall({
        threadId: selectedThread.thread_id,
        clientId: client.client_id,
        clientName: client.name || 'Client',
      });
      setIsVoiceMuted(false);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unable to start call';
      setError(msg);
    }
  };

  const handleMediaClick = (message: ChatSectionMessage) => {
    if (message.media_url) {
      setSelectedMedia({
        url: message.media_url,
        type: message.message_type || 'document',
        filename: message.filename || message.content,
        mimeType: message.mime_type,
      });
      setViewerOpen(true);
    }
  };

  const handleSelectThread = useCallback(
    (thread: Thread) => {
      clearCommunitySelection();
      setSelectedThread(thread);
    },
    [setSelectedThread, clearCommunitySelection]
  );

  const handleSelectCommunity = useCallback(
    async (community: Community) => {
      setSelectedThread(null);
      exitNewChatMode();
      navigate(`/community/${community.community_id}`);
      try {
        await selectCommunity(community);
      } catch {
        navigate('/chat', { replace: true });
      }
    },
    [setSelectedThread, exitNewChatMode, navigate, selectCommunity]
  );

  const handleBackToThreads = useCallback(() => {
    setSelectedThread(null);
    exitNewChatMode();
  }, [setSelectedThread, exitNewChatMode]);

  const showConversationPanel = !isMobile || !!selectedThread || isNewChatMode;
  const showThreadListPanel = !isMobile || !selectedThread && !isNewChatMode;

  const messagingShellSx = {
    flex: 1,
    display: 'flex',
    height: '100%',
    minHeight: 0,
    border: `1px solid ${CWY_COLORS.border}`,
    borderRadius: '8px',
    overflow: 'hidden',
    bgcolor: 'var(--cwy-cardBg, #fff)',
  } as const;

  const renderMessagingLayout = (conversationPanel: React.ReactNode) => (
    <Box sx={messagingShellSx}>
      {showThreadListPanel && (
        <MessagingThreadList
          threads={threads}
          selectedThread={selectedThread}
          loadingThreads={loadingThreads}
          onSelectThread={handleSelectThread}
          communities={communities}
          loadingCommunities={loadingCommunities}
          unreadMap={unreadMap}
          onSelectCommunity={handleSelectCommunity}
        />
      )}
      {showConversationPanel && (
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          {conversationPanel}
        </Box>
      )}
    </Box>
  );

  const renderVoicePanel = () => (
    <AppVoiceCallPanel
      open={voicePanelOpen}
      voiceState={voiceState}
      isMuted={isVoiceMuted}
      onClose={() => {
        setVoicePanelOpen(false);
      }}
      onHangUp={async () => {
        await appTwilioVoiceService.hangUp();
      }}
      onToggleMute={() => {
        const next = !appTwilioVoiceService.isMuted();
        appTwilioVoiceService.setMuted(next);
        setIsVoiceMuted(next);
      }}
      onDigit={(digit) => appTwilioVoiceService.sendDigit(digit)}
    />
  );

  const renderInviteModal = () =>
    inviteModalToken ? (
      <CommunityInviteJoinModal
        open
        token={inviteModalToken}
        preview={inviteModalPreview}
        previewLoading={inviteModalLoading}
        onClose={() => {
          setInviteModalToken(null);
          setInviteModalPreview(null);
        }}
      />
    ) : null;

  // Loading state
  if (loadingThreads && threads.length === 0) {
    return (
      <>
        {renderMessagingLayout(
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        )}
        {renderVoicePanel()}
        {renderInviteModal()}
      </>
    );
  }

  // No thread selected - show new chat UI
  if (!selectedThread) {
    return (
      <>
      {renderMessagingLayout(
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          flex: 1,
          backgroundColor: 'var(--cwy-cardBg, #fff)',
          minHeight: 0,
        }}
      >
        {/* Header */}
        <Box sx={{ 
          px: isMobile ? 2 : 3, 
          py: isMobile ? 1.5 : 2, 
          borderBottom: `1px solid ${CWY_COLORS.border}`,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
          {isMobile && (
            <IconButton size="small" onClick={handleBackToThreads} aria-label="Back to messages">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: isMobile ? '1rem' : '1.125rem' }}>
            New message
          </Typography>
        </Box>

        {/* Empty state content - centered */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          justifyContent: 'center', 
          alignItems: 'center',
          p: 3,
        }}>
          <Box 
            sx={{ 
              width: 72, 
              height: 72, 
              borderRadius: '50%', 
              backgroundColor: 'var(--cwy-bg, #f1f3f4)', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              mb: 3,
            }}
          >
            <ChatBubbleOutline sx={{ fontSize: 36, color: '#606060' }} />
          </Box>
          <Typography variant="h5" sx={{ mb: 1, color: '#0f0f0f', fontWeight: 500, textAlign: 'center' }}>
            {pendingTopic ? pendingTopic.title : 'How can we help you today?'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#606060', textAlign: 'center', maxWidth: 400 }}>
            {pendingTopic
              ? 'Describe your situation and our team will respond as soon as possible.'
              : 'Start a conversation with our support team. We\'re here to assist you with any questions or concerns.'}
          </Typography>
          {pendingTopic && (
            <Chip
              label={pendingTopic.title}
              size="small"
              sx={{ mt: 2, bgcolor: '#FFF3E0', color: '#E65100', fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Input at bottom */}
        <Box 
          sx={{ 
            p: 2, 
            pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
            borderTop: '1px solid var(--cwy-border, #e8eaed)',
            backgroundColor: 'var(--cwy-bg, #f7f7f8)',
            flexShrink: 0,
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
          }}
          onTouchMove={(e) => e.stopPropagation()}
        >
          {error && (
            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            alignItems: 'flex-end',
            maxWidth: 800,
            mx: 'auto',
          }}>
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              placeholder={pendingTopic ? `Message about ${pendingTopic.title}...` : 'Message support...'}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={sending}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                  e.preventDefault();
                  handleStartNewChat();
                }
              }}
              sx={{
                backgroundColor: 'var(--cwy-cardBg, #fff)',
                borderRadius: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  '& fieldset': { borderColor: '#e8eaed' },
                  '&:hover fieldset': { borderColor: '#ccc' },
                  '&.Mui-focused fieldset': { borderColor: '#666' },
                }
              }}
            />
            <IconButton 
              onClick={handleStartNewChat}
              disabled={!newMessage.trim() || sending}
              sx={{ 
                bgcolor: '#606060', 
                color: 'white', 
                width: 44,
                height: 44,
                '&:hover': { bgcolor: '#404040' },
                '&.Mui-disabled': { bgcolor: '#ccc', color: 'white' },
              }}
            >
              {sending ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      </Box>
      )}
      {renderVoicePanel()}
      {renderInviteModal()}
      </>
    );
  }

  // Main chat view
  return (
    <>
    {renderMessagingLayout(
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      flex: 1,
      height: '100%',
      overflow: 'hidden',
      backgroundColor: 'var(--cwy-cardBg, #fff)',
      position: 'relative',
      minHeight: 0,
    }}>
      {/* Chat Header */}
      <Box sx={{ 
        px: isMobile ? 2 : 3, 
        py: isMobile ? 1.5 : 2, 
        borderBottom: `1px solid ${CWY_COLORS.border}`,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          {isMobile && (
            <IconButton size="small" onClick={handleBackToThreads} aria-label="Back to messages">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: isMobile ? '1rem' : '1.125rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {SUPPORT_DISPLAY_NAME}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            onClick={handleCallSupport}
            size="small"
            disabled={!client || !selectedThread}
            aria-label="Call support"
            title="Voice call to support (same queue as phone / WhatsApp)"
            sx={{
              bgcolor: '#e8f5e9',
              color: '#2e7d32',
              '&:hover': { bgcolor: '#c8e6c9' },
              '&.Mui-disabled': { bgcolor: '#f1f3f4', color: '#bdbdbd' },
            }}
          >
            <PhoneIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mx: 2, mt: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {/* Messages Container - scrollable */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--cwy-cardBg, #fff)',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}>
        {loadingMessages ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ 
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            minHeight: '100%',
          }}>
            {messagesWithSeparators.length === 0 ? (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                flex: 1,
                color: '#666'
              }}>
                <Typography>No messages yet</Typography>
              </Box>
            ) : (
              messagesWithSeparators.map((item, idx) => {
                if (item.type === 'separator') {
                  return (
                    <Box key={`sep-${item.label}-${idx}`} sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                      <Chip label={item.label} size="small" sx={{ bgcolor: '#e0e0e0', color: '#555', fontWeight: 500 }} />
                    </Box>
                  );
                }
                
                const message = item.message;
                const isOutgoing = message.type === 'outgoing';
                const dateObj = getMessageDate(message);
                const isValidDate = !isNaN(dateObj.getTime());
                const isFailed = message.status === 'failed';
                const isSending = message.status === 'sending';
                const isCallMessage = message.message_type === 'call';
                const isPaymentMessage = message.message_type === 'payment';
                const isMediaMessage =
                  !!message.media_url &&
                  !!message.message_type &&
                  message.message_type !== 'text' &&
                  message.message_type !== 'call' &&
                  message.message_type !== 'payment';
                const callDurationSecRaw =
                  message.metadata?.talk_time_seconds ??
                  message.metadata?.talkTimeSeconds ??
                  message.metadata?.duration_seconds;
                const callDurationSec =
                  typeof callDurationSecRaw === 'number'
                    ? callDurationSecRaw
                    : typeof callDurationSecRaw === 'string'
                      ? parseInt(callDurationSecRaw, 10)
                      : undefined;

                if (isPaymentMessage) {
                  const metadata = message.metadata || {};
                  const transactionId = metadata.transaction_id as string | undefined;
                  const amount = Number(metadata.amount ?? 0);
                  const currency = String(metadata.currency || 'CAD');
                  const display = resolveChatPaymentDisplayStatus(
                    metadata.payment_status as string | undefined,
                    metadata.expires_at as string | undefined
                  );
                  const isPayingThis = payingTransactionId === transactionId;
                  const timestampLabel = isValidDate
                    ? dateObj.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : undefined;

                  return (
                    <Box
                      key={message.id}
                      sx={{
                        display: 'flex',
                        justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                        width: '100%',
                      }}
                    >
                      <Box sx={{ maxWidth: isMobile ? '85%' : '65%' }}>
                        <ChatPaymentBubble
                          amount={amount}
                          currency={currency}
                          metadata={metadata}
                          content={message.content}
                          timestampLabel={timestampLabel}
                          isOutgoing={isOutgoing}
                          isPaying={isPayingThis}
                          autoOpenDetails={
                            Boolean(transactionId) &&
                            celebrateTransactionId === transactionId &&
                            display === 'completed'
                          }
                          onDetailsAutoOpened={() => setCelebrateTransactionId(null)}
                          onPay={
                            display === 'pay' && transactionId
                              ? () => void handlePayPaymentMessage(transactionId)
                              : undefined
                          }
                        />
                      </Box>
                    </Box>
                  );
                }

                return (
                  <Box
                    key={message.id}
                    sx={{
                      display: 'flex',
                      justifyContent: isOutgoing ? 'flex-end' : 'flex-start',
                      width: '100%',
                    }}
                  >
                    <Paper
                      elevation={0}
                      sx={{
                        p: isMediaMessage ? 0.75 : 1.5,
                        px: isMediaMessage ? 0.75 : 2,
                        maxWidth: isMobile ? '85%' : '65%',
                        minWidth: isMediaMessage ? 200 : undefined,
                        bgcolor: isOutgoing
                          ? (isFailed ? 'rgba(216, 6, 33, 0.2)' : 'var(--cwy-chatOutgoingBg, #d2e3fc)')
                          : 'var(--cwy-bg, #f3f2ef)',
                        color: isOutgoing
                          ? 'var(--cwy-chatOutgoingText, #0f0f0f)'
                          : 'var(--cwy-textPrimary, #0f0f0f)',
                        borderRadius: 2,
                        borderTopRightRadius: isOutgoing ? 4 : 16,
                        borderTopLeftRadius: isOutgoing ? 16 : 4,
                        boxShadow: 'none',
                        opacity: isSending ? 0.7 : 1,
                        overflow: 'hidden',
                      }}
                    >
                      {isCallMessage && (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            py: 0.5,
                            pr: 0.5,
                          }}
                        >
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: isOutgoing ? 'rgba(0,0,0,0.08)' : '#e8f5e9',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <PhoneIcon sx={{ fontSize: 22, color: isOutgoing ? '#2e7d32' : '#2e7d32' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                              {message.content || 'Voice call to support'}
                            </Typography>
                            {callDurationSec != null && !Number.isNaN(callDurationSec) && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                Duration {formatCallDuration(callDurationSec)}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}

                      {isMediaMessage && (
                        <Box sx={{ mb: message.content && message.message_type !== 'audio' ? 1 : 0 }}>
                          <MediaMessageRenderer
                            mediaUrl={message.media_url!}
                            mediaType={message.message_type!}
                            filename={message.filename}
                            mimeType={message.mime_type}
                            fileSize={message.file_size}
                            onMediaClick={() => handleMediaClick(message)}
                            isOutgoing={isOutgoing}
                          />
                        </Box>
                      )}
                      
                      {!isCallMessage &&
                        message.content &&
                        (!isMediaMessage ||
                          (message.content !== message.filename && !message.content.startsWith('📎'))) && (
                        <Box sx={{ px: isMediaMessage ? 1 : 0, pb: isMediaMessage ? 0.5 : 0 }}>
                          <MentionRenderer 
                            content={message.content}
                            onMentionClick={(username: string) => console.log('Mention clicked:', username)}
                          />
                        </Box>
                      )}
                      
                      {isValidDate && (
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'flex-end', 
                          gap: 0.5, 
                          mt: 0.5,
                          px: isMediaMessage ? 1 : 0,
                        }}>
                          {isSending && <CircularProgress size={10} sx={{ color: '#888' }} />}
                          {isFailed && (
                            <Typography variant="caption" sx={{ color: '#d32f2f', fontSize: 11, mr: 0.5 }}>
                              {message.error || 'Failed'}
                            </Typography>
                          )}
                          <Typography variant="caption" sx={{ color: isOutgoing ? 'var(--cwy-chatOutgoingMuted, rgba(255,255,255,0.7))' : 'var(--cwy-textSecondary, #888)', fontSize: 11 }}>
                             {dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Input Area - Fixed at bottom */}
      <Box 
        sx={{ 
          p: 2, 
          pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
          borderTop: `1px solid ${CWY_COLORS.border}`,
          backgroundColor: 'var(--cwy-bg, #fff)',
          flexShrink: 0,
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
        }}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <Box sx={{ 
          display: 'flex', 
          gap: 1, 
          alignItems: 'flex-end',
          maxWidth: 720,
          mx: 'auto',
        }}>
          <MediaUploadButton
            onMediaSelect={handleMediaSend}
            disabled={sending}
          />
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            placeholder="Write a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && newMessage.trim()) {
                e.preventDefault();
                handleSendMessage();
              }
            }}
            sx={{
              backgroundColor: 'var(--cwy-cardBg, #fff)',
              borderRadius: '24px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '24px',
                '& fieldset': { borderColor: CWY_COLORS.border },
                '&:hover fieldset': { borderColor: '#ccc' },
                '&.Mui-focused fieldset': { borderColor: '#0a66c2' },
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
                lineHeight: '1.5',
              }
            }}
          />
          <IconButton 
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sending}
            sx={{ 
              bgcolor: '#0a66c2', 
              color: 'white', 
              width: 40,
              height: 40,
              flexShrink: 0,
              '&:hover': { bgcolor: '#004182' },
              '&.Mui-disabled': { bgcolor: '#ccc', color: 'white' },
            }}
          >
            {sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      
      {/* Media Viewer */}
      {selectedMedia && (
        <MediaViewer
          open={viewerOpen}
          onClose={() => {
            setViewerOpen(false);
            setSelectedMedia(null);
          }}
          mediaUrl={selectedMedia.url}
          mediaType={selectedMedia.type}
          filename={selectedMedia.filename}
          mimeType={selectedMedia.mimeType}
        />
      )}

      {renderVoicePanel()}
      {renderInviteModal()}
    </Box>
    )}
    </>
  );
};
